import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Tabs, Tab, Button, ProgressBar, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import styles from './CompanyRegistration.module.css';
import DataTab from './Tabs/DataTab';
import ContactsTab from './Tabs/ContactsTab';
import AddressesTab from './Tabs/AddressesTab';
import DpoTab from './Tabs/DpoTab';
import LicensesTab from './Tabs/LicensesTab';
import AssinaturaTab from './Tabs/AssinaturaTab';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';
import { useT } from '../../i18n';
import { FiX, FiSave, FiArrowLeft, FiEdit2, FiPower } from '../../utils/icons';

// Função de validação de CNPJ
const validateCNPJ = (cnpj: string): boolean => {
  const cnpjNumbers = cnpj.replace(/\D/g, '');
  if (cnpjNumbers.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpjNumbers)) return false;
  let length = cnpjNumbers.length - 2;
  let numbers = cnpjNumbers.substring(0, length);
  const digits = cnpjNumbers.substring(length);
  let sum = 0;
  let pos = length - 7;
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  length = length + 1;
  numbers = cnpjNumbers.substring(0, length);
  sum = 0;
  pos = length - 7;
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return result === parseInt(digits.charAt(1));
};

// Interfaces
export interface ICompanyData {
  id?: number;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  inscricao_estadual: string;
  inscricao_municipal: string;
  ramo_atuacao: string[];
  regulamentacao_setorial: string[];
  dados_complementares: string;
  tipo: string;
  empresa_pai_id?: number | null;
  consultoria_id?: number | null;
  status?: string;
}
export interface IContact { id: number; nome: string; email: string; telefone: string; departamento: string; cargo: string; }
export interface IAddress { id: number; tipo_endereco: string; cep: string; endereco: string; numero: string; bairro: string; cidade: string; estado: string; }
export interface ILicense { id: number; tipo: 'Módulos' | 'Serviços' | 'Mentoria'; status: 'Ativa' | 'Inativa'; }
export interface IDpo { nome: string; email: string; telefone: string; departamento: string; }

// ── Modal de desativação (exige justificativa) ──────────────────────────────────
interface DeactivateModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (justificativa: string) => void;
  saving: boolean;
}

const DeactivateModal: React.FC<DeactivateModalProps> = ({ show, onHide, onConfirm, saving }) => {
  const [justificativa, setJustificativa] = useState('');
  const t = useT();
  useEffect(() => { if (show) setJustificativa(''); }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title className="fs-6">{t('companyRegistration.deactivateModal.title')}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          {t('companyRegistration.deactivateModal.text')}
        </p>
        <Form.Group>
          <Form.Label>{t('companyRegistration.deactivateModal.justificativaLabel')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={justificativa}
            onChange={e => setJustificativa(e.target.value)}
            placeholder={t('companyRegistration.deactivateModal.justificativaPlaceholder')}
            autoFocus
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}><FiX /> {t('companyRegistration.buttons.cancel')}</Button>
        <Button variant="danger" onClick={() => onConfirm(justificativa)} disabled={!justificativa.trim() || saving}>
          {saving ? <Spinner as="span" size="sm" animation="border" /> : <><FiPower /> {t('companyRegistration.deactivateModal.confirm')}</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const CompanyRegistration: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dados');
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { tipoEmpresa, empresaId } = useInterfaceStore();
  const t = useT();
  const tipoPessoa = (() => {
    try { return JSON.parse(localStorage.getItem('usuarioData') || '{}').tipo_pessoa || ''; }
    catch { return ''; }
  })();
  const showAssinatura = !!id && (tipoEmpresa === '4Comply' || tipoPessoa === 'Administrador');

  // Entrar no cadastro de uma empresa existente sempre abre em modo visualização;
  // "Editar" habilita a edição. Uma empresa nova já entra editável.
  const [modoEdicao, setModoEdicao] = useState(!id);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const [companyData, setCompanyData] = useState<ICompanyData>({
    razao_social: '', nome_fantasia: '', cnpj: '', inscricao_estadual: '', inscricao_municipal: '',
    ramo_atuacao: [], regulamentacao_setorial: [], dados_complementares: '',
    tipo: 'Empresa Fim', empresa_pai_id: null, consultoria_id: null, status: 'Ativo',
  });
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [licenses, setLicenses] = useState<ILicense[]>([]);
  const [dpo, setDpo] = useState<IDpo>({ nome: '', email: '', telefone: '', departamento: '' });
  const [cnpjError, setCnpjError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Consultoria visualiza licenças das empresas clientes (somente leitura), nunca a própria
  const isClientCompany = !!id && tipoEmpresa === 'Consultoria' && (
    companyData.consultoria_id === empresaId || companyData.empresa_pai_id === empresaId
  );
  const showLicencas = tipoEmpresa === '4Comply' || isClientCompany;
  const licencasReadOnly = isClientCompany;
  const isInativa = companyData.status === 'Inativo';

  const carregarEmpresa = async () => {
    if (!id) return;
    try {
      const response = await api.getCompany(Number(id));
      if (response.success && response.data) {
        setCompanyData(response.data);
        setContacts(response.data.contatos || []);
        setAddresses(response.data.enderecos || []);
        setLicenses(response.data.licencas || []);
        setDpo(response.data.dpo || { nome: '', email: '', telefone: '', departamento: '' });
      }
    } catch (error) {
      console.error('Erro de rede:', error);
    }
  };

  useEffect(() => {
    carregarEmpresa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async () => {
    if (!companyData.razao_social.trim()) {
      notify.warning(t('companyRegistration.notify.razaoSocialRequired'));
      return;
    }
    if (!validateCNPJ(companyData.cnpj)) {
      notify.warning(t('companyRegistration.notify.cnpjInvalid'));
      return;
    }
    if (dpo.nome || dpo.email) {
      if (!dpo.nome.trim()) { notify.warning(t('companyRegistration.notify.dpoNomeRequired')); return; }
      if (!dpo.email.trim()) { notify.warning(t('companyRegistration.notify.dpoEmailRequired')); return; }
    }

    const fullCompanyData = {
      ...companyData,
      contatos: contacts.map(({ id, ...rest }) => rest),
      enderecos: addresses.map(({ id, ...rest }) => rest),
      licencas: tipoEmpresa === '4Comply' ? licenses.map(({ id, ...rest }) => rest) : [],
      dpo: (dpo.nome || dpo.email) ? dpo : undefined,
    };

    setIsSubmitting(true);
    try {
      const isEditingMode = !!id;
      const response = isEditingMode
        ? await api.updateCompany(Number(id), fullCompanyData)
        : await api.createCompany(fullCompanyData);

      if (response.success) {
        notify.success(isEditingMode
          ? t('companyRegistration.notify.updateSuccess')
          : t('companyRegistration.notify.createSuccess', { id: response.data?.empresaId }));
        if (isEditingMode) {
          setModoEdicao(false);
          await carregarEmpresa();
        } else {
          navigate('/app/empresas/list');
        }
      } else {
        const errorMsg = response.data?.error || response.data?.message || response.error || '';
        if (errorMsg.includes('CNPJ já cadastrado') || errorMsg.includes('CNPJ já em uso')) {
          setCnpjError(t('companyRegistration.notify.cnpjDuplicate'));
        } else {
          notify.error(t('companyRegistration.notify.saveError', { error: errorMsg }));
        }
      }
    } catch (error) {
      console.error('Erro de conexão:', error);
      notify.error(t('companyRegistration.notify.connectionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    carregarEmpresa();
    setModoEdicao(false);
  };

  const handleDeactivate = async (justificativa: string) => {
    if (!id) return;
    setDeactivating(true);
    try {
      const response = await api.deactivateCompany(Number(id), { justificativa, status: 'Inativo' });
      if (response.success) {
        notify.success(t('companyRegistration.notify.deactivateSuccess'));
        setCompanyData(prev => ({ ...prev, status: 'Inativo' }));
        setModoEdicao(false);
        setShowDeactivateModal(false);
      } else {
        notify.error((response.data as any)?.error || response.error || t('companyRegistration.notify.deactivateError'));
      }
    } catch {
      notify.error(t('companyRegistration.notify.deactivateConnectionError'));
    } finally {
      setDeactivating(false);
    }
  };

  const totalSteps = (showLicencas ? 1 : 0) + (showAssinatura ? 1 : 0) + 4;
  const stepMap: { [key: string]: number } = (() => {
    const map: { [key: string]: number } = { dados: 1, contatos: 2, enderecos: 3 };
    let next = 4;
    if (showLicencas) { map['licencas'] = next++; }
    if (showAssinatura) { map['assinatura'] = next++; }
    map['dpo'] = next;
    return map;
  })();
  const currentStep = stepMap[activeTab] || 1;
  const progress = (currentStep / totalSteps) * 100;

  return (
    <Container fluid className={styles.container}>
      <Row className={styles.header}>
        <Col>
          <h1 className={styles.title}>
            {!id ? t('companyRegistration.titleNew') : modoEdicao ? t('companyRegistration.titleEdit') : t('companyRegistration.titleView')}
          </h1>
          {modoEdicao && (
            <>
              <p className={styles.progressText}>{t('companyRegistration.stepLabel', { step: currentStep, total: totalSteps })}</p>
              <ProgressBar now={progress} label={`${Math.round(progress)}%`} />
            </>
          )}
        </Col>
      </Row>
      {!!id && !modoEdicao && !isInativa && (
        <Alert variant="info" className="mb-3">
          {t('companyRegistration.readOnlyAlert')}
        </Alert>
      )}
      {isInativa && (
        <Alert variant="warning" className="mb-3">
          {t('companyRegistration.inactiveAlertBefore')}<strong>{t('companyRegistration.inactiveAlertBold')}</strong>{t('companyRegistration.inactiveAlertAfter')}
        </Alert>
      )}
      <fieldset disabled={!modoEdicao}>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'dados')} id="company-registration-tabs" className="mb-3">
          <Tab eventKey="dados" title={t('companyRegistration.tabs.dados')}>
            <DataTab data={companyData} setData={setCompanyData} cnpjError={cnpjError} setCnpjError={setCnpjError} tipoEmpresaLogada={tipoEmpresa} empresaId={empresaId} />
          </Tab>
          <Tab eventKey="contatos" title={t('companyRegistration.tabs.contatos')}>
            <ContactsTab contacts={contacts} setContacts={setContacts} />
          </Tab>
          <Tab eventKey="enderecos" title={t('companyRegistration.tabs.enderecos')}>
            <AddressesTab addresses={addresses} setAddresses={setAddresses} empresaId={companyData.id ?? empresaId} />
          </Tab>
          {showLicencas && (
            <Tab eventKey="licencas" title={t('companyRegistration.tabs.licencas')}>
              <LicensesTab licenses={licenses} setLicenses={setLicenses} readOnly={licencasReadOnly} />
            </Tab>
          )}
          {showAssinatura && (
            <Tab eventKey="assinatura" title={t('companyRegistration.tabs.assinatura')}>
              <AssinaturaTab empresaId={Number(id)} />
            </Tab>
          )}
          <Tab eventKey="dpo" title={t('companyRegistration.tabs.dpo')}>
            <DpoTab dpo={dpo} setDpo={setDpo} />
          </Tab>
        </Tabs>
      </fieldset>

      <Row>
        <Col className={`${styles.buttonContainer} d-flex gap-2 justify-content-end`}>
          {!id && (
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Spinner as="span" animation="border" size="sm" /> : <><FiSave /> {t('companyRegistration.buttons.save')}</>}
            </Button>
          )}
          {!!id && !modoEdicao && (
            <>
              <Button variant="secondary" onClick={() => navigate('/app/empresas/list')}>
                <FiArrowLeft /> {t('companyRegistration.buttons.backToList')}
              </Button>
              {!isInativa && (
                <>
                  <Button variant="primary" onClick={() => setModoEdicao(true)}><FiEdit2 /> {t('companyRegistration.buttons.edit')}</Button>
                  <Button variant="outline-danger" onClick={() => setShowDeactivateModal(true)}><FiPower /> {t('companyRegistration.buttons.deactivate')}</Button>
                </>
              )}
            </>
          )}
          {!!id && modoEdicao && (
            <>
              <Button variant="secondary" onClick={handleCancelEdit} disabled={isSubmitting}><FiX /> {t('companyRegistration.buttons.cancel')}</Button>
              <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? <Spinner as="span" animation="border" size="sm" /> : <><FiSave /> {t('companyRegistration.buttons.save')}</>}
              </Button>
            </>
          )}
        </Col>
      </Row>

      <DeactivateModal
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
        onConfirm={handleDeactivate}
        saving={deactivating}
      />
    </Container>
  );
};

export default CompanyRegistration;
